import { useEffect, useRef, useState } from 'react';
import appConfig from 'appConfig';
import { appInsights } from 'utils/appInsights';
import { useUserInfo } from 'hooks/useUserInfo';

interface VersionCheckConfig {
  /** Check interval in milliseconds (default: 5 minutes) */
  checkInterval?: number;
  /** Whether to auto-reload without user confirmation */
  autoReload?: boolean;
  /** Check for updates when user returns to tab */
  checkOnVisibilityChange?: boolean;
  /** Callback when new version is detected */
  onNewVersion?: (currentVersion: string, newVersion: string) => void;
}

/**
 * Hook to check for new app versions by comparing the version meta tag in index.html
 * 
 * How it works:
 * 1. Fetches index.html periodically
 * 2. Extracts version from meta tag
 * 3. Compares with current version
 * 4. Notifies or auto-reloads when new version detected
 * 
 * @example
 * ```tsx
 * // In your App.tsx or root component
 * const { hasNewVersion, currentVersion, newVersion, reloadApp } = useVersionCheck({
 *   checkInterval: 5 * 60 * 1000, // Check every 5 minutes
 *   onNewVersion: (current, latest) => {
 *     console.log(`New version ${latest} available (current: ${current})`);
 *   }
 * });
 * 
 * if (hasNewVersion) {
 *   return <UpdatePrompt onReload={reloadApp} />;
 * }
 * ```
 */
export function useVersionCheck(config: VersionCheckConfig = {}) {
  const {
    checkInterval = 5 * 60 * 1000, // Default: 5 minutes
    autoReload = false,
    checkOnVisibilityChange = false,
    onNewVersion
  } = config;

  const { userName } = useUserInfo();
  const [hasNewVersion, setHasNewVersion] = useState(false);
  const [newVersion, setNewVersion] = useState<string>('');
  const currentVersion = appConfig.version;
  const intervalRef = useRef<NodeJS.Timeout>();
  const hasNotifiedRef = useRef(false);
  const notifiedVersionRef = useRef<string>(''); // Track which version we notified about

  const checkForUpdates = async () => {
    // Check if verbose logging is enabled
    const isVerbose = sessionStorage.getItem('versionCheckVerbose') === 'true';
    
    if (isVerbose) {
      console.log('%c[VersionCheck] 🔍 Starting version check...', 'color: #2196f3; font-weight: bold');
      console.log('[VersionCheck] Current version:', currentVersion);
      console.log('[VersionCheck] Check interval:', checkInterval / 1000, 'seconds');
    }
    
    try {
      // Check if there's a simulated version (for testing)
      const simulatedVersion = sessionStorage.getItem('versionCheckSimulatedVersion');
      let html: string;
      
      if (simulatedVersion) {
        // Use simulated version (for testing)
        if (isVerbose) {
          console.log('%c[VersionCheck] 🎭 Using simulated version (testing mode)', 'color: #ff9800; font-weight: bold');
          console.log('[VersionCheck] Simulated version:', simulatedVersion);
        }
        
        // Create mock HTML with simulated version
        html = `<html><head><meta name="app-version" content="${simulatedVersion}" /></head></html>`;
      } else {
        // Normal flow: Fetch index.html from server
        // Use timestamp + random to bust Azure CDN cache
        const timestamp = new Date().getTime();
        const random = Math.random().toString(36).substring(7);
        
        if (isVerbose) {
          console.log('[VersionCheck] Fetching /index.html?v=' + timestamp + '&r=' + random);
        }
        
        const response = await fetch(`/index.html?v=${timestamp}&r=${random}`, {
          method: 'GET',
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate, max-age=0',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });

        if (!response.ok) {
          console.warn('[VersionCheck] Failed to fetch index.html:', response.status);
          if (isVerbose) {
            console.error('[VersionCheck] ❌ Fetch failed with status:', response.status, response.statusText);
          }
          return;
        }

        if (isVerbose) {
          console.log('[VersionCheck] ✅ Fetched index.html successfully');
        }

        html = await response.text();
      }
      
      // Extract version from meta tag: <meta name="app-version" content="..." />
      const versionMatch = html.match(/<meta\s+name=["']app-version["']\s+content=["']([^"']+)["']/i);
      
      if (versionMatch && versionMatch[1]) {
        const latestVersion = versionMatch[1];
        
        if (isVerbose) {
          console.log('[VersionCheck] Server version:', latestVersion);
          console.log('[VersionCheck] Version comparison:', {
            current: currentVersion,
            server: latestVersion,
            matches: latestVersion === currentVersion
          });
        }
        
        // Compare versions (simple string comparison)
        if (latestVersion !== currentVersion) {
          // Check if this is a different version than we previously notified about
          if (latestVersion !== notifiedVersionRef.current) {
            // Reset notification flag for new version
            hasNotifiedRef.current = false;
            
            if (isVerbose) {
              console.log('[VersionCheck] 🔄 New version detected, resetting notification flag');
            }
          }
          
          if (!hasNotifiedRef.current) {
            // Check if user previously dismissed this version (in this session)
            const dismissedVersion = sessionStorage.getItem('dismissedVersion');
            if (dismissedVersion === latestVersion) {
              console.log('[VersionCheck] User previously dismissed version:', latestVersion);
              if (isVerbose) {
                console.log('[VersionCheck] ⏭️ Skipping notification (user dismissed this version)');
              }
              // Don't show notification again for this version in this session
              return;
            }

            console.log('%c[VersionCheck] 🎉 New version detected!', 'color: #4caf50; font-weight: bold', {
              current: currentVersion,
              latest: latestVersion
            });

            // Track in Application Insights
            appInsights.trackEvent({
              name: 'VersionControl_UpdateReceived',
              properties: {
                user: userName || 'Unknown',
                version: latestVersion || 'Unknown',
                currentVersion: currentVersion || 'Unknown',
                isMandatory: 'false',
                source: 'polling',
                autoReload: autoReload.toString(),
                timestamp: new Date().toISOString()
              }
            });

            setNewVersion(latestVersion);
            setHasNewVersion(true);
            hasNotifiedRef.current = true;
            notifiedVersionRef.current = latestVersion; // Track which version we notified about

            // Trigger callback
            onNewVersion?.(currentVersion, latestVersion);

            // Auto-reload if configured
            if (autoReload) {
              console.log('[VersionCheck] Auto-reloading app...');
              reloadApp();
            }
          } else {
            if (isVerbose) {
              console.log('[VersionCheck] ℹ️ Update already notified (hasNotifiedRef is true)');
              console.log('[VersionCheck] Notified version:', notifiedVersionRef.current);
            }
          }
        } else {
          if (isVerbose) {
            console.log('[VersionCheck] ✅ No update needed (versions match)');
          }
        }
      } else {
        console.warn('[VersionCheck] Could not find app-version meta tag in index.html');
        if (isVerbose) {
          console.error('[VersionCheck] ❌ Version meta tag not found in HTML response');
        }
      }
      
      if (isVerbose) {
        console.log('%c[VersionCheck] ✅ Check complete', 'color: #2196f3; font-weight: bold');
        console.log('[VersionCheck] Next check in:', checkInterval / 1000, 'seconds');
      }
    } catch (error) {
      console.error('[VersionCheck] Error checking for updates:', error);
      if (isVerbose) {
        console.error('[VersionCheck] ❌ Exception during check:', error);
      }
    }
  };

  const reloadApp = () => {
    // Clear dismissed version from sessionStorage before reloading
    try {
      sessionStorage.removeItem('dismissedVersion');
    } catch (error) {
      console.error('[VersionCheck] Failed to clear dismissed version:', error);
    }
    
    // Clear cache and reload
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => caches.delete(name));
      }).then(() => {
        (window as Window).location.reload();
      });
    } else {
      (window as Window).location.reload();
    }
  };

  useEffect(() => {
    // Initial check after 30 seconds (give app time to fully load)
    const initialTimeout = setTimeout(() => {
      checkForUpdates();
    }, 30000);

    // Set up periodic checks
    intervalRef.current = setInterval(() => {
      checkForUpdates();
    }, checkInterval);

    // Set up visibility change listener if enabled
    let visibilityHandler: (() => void) | undefined;
    if (checkOnVisibilityChange) {
      visibilityHandler = () => {
        if (!document.hidden) {
          const isVerbose = sessionStorage.getItem('versionCheckVerbose') === 'true';
          if (isVerbose) {
            console.log('%c[VersionCheck] 👁️ User returned to tab, checking for updates...', 'color: #ff9800; font-weight: bold');
          } else {
            console.log('[VersionCheck] User returned to tab, checking for updates...');
          }
          checkForUpdates();
        }
      };
      document.addEventListener('visibilitychange', visibilityHandler);
    }

    return () => {
      clearTimeout(initialTimeout);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (visibilityHandler) {
        document.removeEventListener('visibilitychange', visibilityHandler);
      }
    };
  }, [checkInterval, currentVersion, checkOnVisibilityChange]);

  return {
    hasNewVersion,
    currentVersion,
    newVersion,
    reloadApp,
    checkNow: checkForUpdates
  };
}

